import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { MessageSquare, Send, Users, RefreshCw, Search, Loader2, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALL = '__all__';

const CATEGORY_LABELS = {
    broadcast: 'Broadcast',
    payment_reminder: 'Payment Reminder',
    renewal_reminder: 'Renewal Reminder',
    payment_confirmation: 'Payment Confirmation',
    waiting_approval: 'Application Approved',
    alert_late: 'Arrived Late',
    alert_left_early: 'Left Early',
};

const statusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'delivered') return <Badge className="bg-[#10B981] text-white">Delivered</Badge>;
    if (s === 'failed' || s === 'undelivered') return <Badge className="bg-[#EF4444] text-white">{s === 'failed' ? 'Failed' : 'Undelivered'}</Badge>;
    return <Badge className="bg-[#F59E0B] text-white capitalize">{status || 'Queued'}</Badge>;
};

const CheckList = ({ title, items, selected, onToggle, testId }) => (
    <div className="space-y-2">
        <Label>{title}</Label>
        <div className="border border-[#E5E7EB] rounded-md max-h-44 overflow-y-auto p-2 space-y-1" data-testid={testId}>
            {items.length === 0 && <p className="text-xs text-[#9CA3AF] p-1">None</p>}
            {items.map(it => (
                <label key={it.value} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#F9FAFB] rounded px-1 py-0.5">
                    <input type="checkbox" checked={selected.includes(it.value)} onChange={() => onToggle(it.value)} />
                    <span>{it.label}</span>
                </label>
            ))}
        </div>
        {selected.length > 0 && <p className="text-xs text-[#064E3B]">{selected.length} selected</p>}
    </div>
);

const SmsCenter = () => {
    const [programs, setPrograms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [message, setMessage] = useState('');
    const [selBranches, setSelBranches] = useState([]);
    const [selPrograms, setSelPrograms] = useState([]);
    const [selClasses, setSelClasses] = useState([]);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [sending, setSending] = useState(false);
    const [logs, setLogs] = useState([]);
    const [logsLoading, setLogsLoading] = useState(false);
    const [logSearch, setLogSearch] = useState('');
    const [logCategory, setLogCategory] = useState(ALL);
    const [logStatus, setLogStatus] = useState(ALL);

    useEffect(() => {
        (async () => {
            try {
                const [pRes, cRes] = await Promise.all([
                    axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/classes`, { withCredentials: true }),
                ]);
                setPrograms(pRes.data || []);
                setClasses(cRes.data || []);
            } catch { toast.error('Failed to load filters'); }
        })();
    }, []);

    const fetchLogs = useCallback(async () => {
        setLogsLoading(true);
        try {
            const params = {};
            if (logSearch) params.search = logSearch;
            if (logCategory !== ALL) params.category = logCategory;
            if (logStatus !== ALL) params.status = logStatus;
            const res = await axios.get(`${API_URL}/api/sms/logs`, { params, withCredentials: true });
            setLogs(res.data || []);
        } catch { toast.error('Failed to load SMS logs'); }
        finally { setLogsLoading(false); }
    }, [logSearch, logCategory, logStatus]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const branchOptions = useMemo(() =>
        Array.from(new Set(programs.map(p => p.location).filter(Boolean))).sort()
            .map(b => ({ value: b, label: b })), [programs]);
    const programOptions = useMemo(() =>
        [...programs].sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
            .map(p => ({ value: p.id, label: `${p.name}${p.location ? ` (${p.location})` : ''}` })), [programs]);
    const classOptions = useMemo(() =>
        classes.map(c => ({ value: c.id, label: c.name || c.english_name || c.arabic_name || c.code || c.id })), [classes]);

    const toggle = (setter) => (v) => setter(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);

    const handlePreview = async () => {
        setPreviewing(true);
        try {
            const res = await axios.post(`${API_URL}/api/sms/broadcast/preview`,
                { branches: selBranches, program_ids: selPrograms, class_ids: selClasses }, { withCredentials: true });
            setPreview(res.data);
        } catch { toast.error('Preview failed'); }
        finally { setPreviewing(false); }
    };

    const handleSend = async () => {
        if (!message.trim()) { toast.error('Write a message first'); return; }
        setSending(true);
        try {
            const res = await axios.post(`${API_URL}/api/sms/broadcast`,
                { message: message.trim(), branches: selBranches, program_ids: selPrograms, class_ids: selClasses },
                { withCredentials: true });
            toast.success(`SMS sent to ${res.data.sent} guardians${res.data.failed ? ` (${res.data.failed} failed)` : ''}`);
            setMessage(''); setPreview(null);
            fetchLogs();
        } catch (e) { toast.error(e.response?.data?.detail || 'Broadcast failed'); }
        finally { setSending(false); }
    };

    const charCount = message.length;
    const segments = charCount === 0 ? 0 : Math.ceil(charCount / 160);

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="sms-center-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit'] flex items-center gap-2"><MessageSquare className="h-6 w-6 text-[#064E3B]" />SMS Center</h1>
                    <p className="text-[#4B5563]">Send SMS to guardians (sender: NurulHuda) and track delivery</p>
                </div>

                <Tabs defaultValue="broadcast">
                    <TabsList>
                        <TabsTrigger value="broadcast" data-testid="tab-sms-broadcast"><Send className="h-4 w-4 mr-2" />Broadcast</TabsTrigger>
                        <TabsTrigger value="logs" data-testid="tab-sms-logs"><MessageSquare className="h-4 w-4 mr-2" />Logs ({logs.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="broadcast">
                        <Card className="card-surface">
                            <CardHeader><CardTitle className="text-lg font-['Outfit']">Compose Broadcast</CardTitle></CardHeader>
                            <CardContent className="space-y-5">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <CheckList title="Branches (empty = all)" items={branchOptions} selected={selBranches} onToggle={toggle(setSelBranches)} testId="sms-branch-list" />
                                    <CheckList title="Programs (empty = all)" items={programOptions} selected={selPrograms} onToggle={toggle(setSelPrograms)} testId="sms-program-list" />
                                    <CheckList title="Classes (empty = all)" items={classOptions} selected={selClasses} onToggle={toggle(setSelClasses)} testId="sms-class-list" />
                                </div>
                                <div className="space-y-2">
                                    <Label>Message</Label>
                                    <textarea
                                        className="w-full min-h-[120px] rounded-md border border-[#D1D5DB] p-3 text-sm focus:ring-2 focus:ring-[#064E3B] focus:border-transparent"
                                        value={message} onChange={e => setMessage(e.target.value)}
                                        placeholder="Type the SMS message guardians will receive..."
                                        data-testid="sms-broadcast-message"
                                    />
                                    <p className="text-xs text-[#4B5563]">{charCount} characters ≈ {segments} SMS segment{segments === 1 ? '' : 's'} per recipient</p>
                                </div>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Button variant="outline" onClick={handlePreview} disabled={previewing} data-testid="sms-preview-btn">
                                        {previewing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}Preview Recipients
                                    </Button>
                                    <Button className="bg-[#064E3B] hover:bg-[#047857]" onClick={handleSend} disabled={sending || !message.trim()} data-testid="sms-send-btn">
                                        {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                                        Send{preview ? ` to ${preview.will_send} guardians` : ''}
                                    </Button>
                                </div>
                                {preview && (
                                    <div className="bg-[#FAF9F6] border border-[#E5E7EB] rounded-lg p-4 space-y-2" data-testid="sms-preview-panel">
                                        <div className="flex flex-wrap gap-2">
                                            <Badge variant="secondary"><Users className="h-3 w-3 mr-1" />{preview.total_guardians} guardians matched</Badge>
                                            <Badge className="bg-[#064E3B] text-white">{preview.will_send} will receive SMS</Badge>
                                            {preview.no_valid_mobile > 0 && <Badge className="bg-[#F59E0B] text-white">{preview.no_valid_mobile} without valid mobile</Badge>}
                                        </div>
                                        {preview.recipients?.length > 0 && (
                                            <p className="text-xs text-[#4B5563]">
                                                {preview.recipients.slice(0, 10).map(r => r.name).join(', ')}{preview.will_send > 10 ? ` +${preview.will_send - 10} more` : ''}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="logs">
                        <Card className="card-surface">
                            <CardContent className="p-4 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                                        <Input placeholder="Search name, mobile, message..." value={logSearch} onChange={e => setLogSearch(e.target.value)} className="pl-9" data-testid="sms-log-search" />
                                    </div>
                                    <Select value={logCategory} onValueChange={setLogCategory}>
                                        <SelectTrigger data-testid="sms-log-category-filter"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={ALL}>All categories</SelectItem>
                                            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Select value={logStatus} onValueChange={setLogStatus}>
                                        <SelectTrigger data-testid="sms-log-status-filter"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value={ALL}>All statuses</SelectItem>
                                            <SelectItem value="delivered">Delivered</SelectItem>
                                            <SelectItem value="sent">Sent</SelectItem>
                                            <SelectItem value="queued">Queued</SelectItem>
                                            <SelectItem value="failed">Failed</SelectItem>
                                            <SelectItem value="undelivered">Undelivered</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Button variant="outline" onClick={fetchLogs} disabled={logsLoading} data-testid="sms-log-refresh">
                                        {logsLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}Refresh
                                    </Button>
                                </div>
                                {logs.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader><TableRow>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Recipient</TableHead>
                                                <TableHead>Mobile</TableHead>
                                                <TableHead>Category</TableHead>
                                                <TableHead>Message</TableHead>
                                                <TableHead>Status</TableHead>
                                            </TableRow></TableHeader>
                                            <TableBody>
                                                {logs.map(l => (
                                                    <TableRow key={l.id} data-testid={`sms-log-row-${l.id}`}>
                                                        <TableCell className="text-xs tabular-nums whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</TableCell>
                                                        <TableCell className="text-sm font-medium">{l.recipient_name || '—'}</TableCell>
                                                        <TableCell className="font-mono text-xs">{l.to || l.to_raw || '—'}</TableCell>
                                                        <TableCell><Badge variant="outline" className="text-xs">{CATEGORY_LABELS[l.category] || l.category}</Badge></TableCell>
                                                        <TableCell className="text-xs max-w-[320px]">
                                                            <span title={l.body}>{l.body?.length > 90 ? `${l.body.slice(0, 90)}…` : l.body}</span>
                                                            {l.error && <p className="text-[#EF4444] mt-1">{l.error}</p>}
                                                        </TableCell>
                                                        <TableCell>{statusBadge(l.status)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12">
                                        <MessageSquare className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                        <p className="text-[#4B5563]">No SMS logs yet</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
};

export default SmsCenter;
